import React, { useState } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import { verifyProofOnChain } from '../utils/verifyProofOnChain';
import AdPlaceholder from './AdPlaceholder';

const ProofDecoder = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE || 'https://plexo-natural-api.onrender.com';

  const decodeProof = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    setVerificationStatus(null);

    try {
      let txHash = '';
      let proofHash = '';

      // 1. Extrai txHash de URL ou input direto
      if (input.includes('polygonscan.com/tx/') || input.includes('blockscout.com')) {
        txHash = input.split('/tx/')[1]?.split('?')[0] || '';
      } else if (input.startsWith('0x') && input.length === 66) {
        txHash = input;
      } else if (input.startsWith('0x') && input.length === 68) {
        proofHash = input;
      } else {
        throw new Error('Formato inválido. Use txHash ou URL do explorer.');
      }

      let message = '';
      let videoUrl = null;
      let timestamp = '';
      let author = '';
      let onChainData = null;

      if (txHash) {
        // 2. PRIMEIRO: Verificar na blockchain (fonte de verdade)
        setVerificationStatus({ status: 'verifying', message: 'Verificando na blockchain...' });
        const chainVerification = await verifyProofOnChain(txHash);
        
        if (chainVerification.success) {
          onChainData = chainVerification;
          proofHash = chainVerification.proofHash;
          setVerificationStatus({ 
            status: 'verified', 
            message: '✓ Verificado na blockchain',
            data: chainVerification
          });
        } else {
          setVerificationStatus({ 
            status: 'error', 
            message: '⚠ Transação não encontrada ou inválida na blockchain',
            error: chainVerification.error
          });
        }

        // 3. DEPOIS: Buscar dados complementares no backend (Firestore)
        try {
          const res = await axios.get(`${API_BASE}/api/decode-proof/${txHash}`);
          const data = res.data.proof || res.data;

          message = data.message || 'Mensagem não encontrada';
          videoUrl = data.videoUrl || null;
          timestamp = data.timestamp || (onChainData?.timestamp ? new Date(onChainData.timestamp * 1000).toISOString() : 'N/A');
          author = data.authorName || data.author || 'Anônimo';
        } catch (backendErr) {
          console.warn('Backend não retornou dados, usando apenas dados on-chain:', backendErr);
          // Se backend falhar, usar dados on-chain
          if (onChainData) {
            timestamp = onChainData.timestamp ? new Date(onChainData.timestamp * 1000).toISOString() : 'N/A';
            author = onChainData.user || 'Endereço desconhecido';
            message = 'Proof of Life verificada na blockchain';
          }
        }

      } else if (proofHash) {
        // 3. Busca direta por proofHash (apenas backend por enquanto)
        const snap = await axios.get(`${API_BASE}/api/proof-by-hash/${proofHash}`);
        const data = snap.data;

        message = data.message || 'Mensagem não encontrada';
        videoUrl = data.videoUrl || null;
        timestamp = data.timestamp || 'N/A';
        author = data.author || 'Anônimo';
      }

      setResult({ 
        message, 
        videoUrl, 
        timestamp, 
        author, 
        txHash, 
        proofHash,
        onChainData,
        verified: onChainData?.success || false
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao decodificar');
      setVerificationStatus({ 
        status: 'error', 
        message: 'Erro ao verificar',
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Container principal com sidebars para anúncios */}
      <div className="max-w-7xl mx-auto px-4 py-8 flex gap-6">
        {/* SIDEBAR ESQUERDA - ANÚNCIO (apenas desktop) */}
        <aside className="hidden lg:block flex-shrink-0">
          <AdPlaceholder position="sidebar" />
        </aside>

        {/* CONTEÚDO PRINCIPAL */}
        <main className="flex-1 max-w-2xl mx-auto">
          <div className="bg-white border-2 border-violet-200 rounded-xl p-6 shadow-md">
            <h2 className="text-2xl font-bold text-center mb-2 text-black">Decodificador de Prova</h2>
            <p className="text-sm text-gray-600 text-center mb-6">
              Cole txHash, proofHash ou URL do explorer
            </p>

            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="0x8c38f4c... ou https://polygonscan.com/tx/..."
              className="w-full p-3 rounded-lg bg-gray-50 border border-gray-300 text-black placeholder-gray-400 mb-4 focus:outline-none focus:ring-2 focus:ring-violet-500"
              onKeyPress={(e) => e.key === 'Enter' && decodeProof()}
            />

            <button
              onClick={decodeProof}
              disabled={loading || !input}
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-green-500 rounded-lg font-bold disabled:opacity-50 transition text-white hover:from-violet-700 hover:to-green-600"
            >
              {loading ? 'Decodificando...' : 'Decodificar Prova'}
            </button>

            {/* ✅ PRESERVADO: Mensagens de erro com design atualizado */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                <p><b>Erro:</b> {error}</p>
              </div>
            )}

            {/* ✅ PRESERVADO: Status de verificação com design atualizado */}
            {verificationStatus && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${
                verificationStatus.status === 'verified' 
                  ? 'bg-green-50 border-2 border-green-500 text-green-800' 
                  : verificationStatus.status === 'error'
                  ? 'bg-red-50 border-2 border-red-500 text-red-800'
                  : 'bg-yellow-50 border-2 border-yellow-500 text-yellow-800'
              }`}>
                <p className="font-bold">
                  {verificationStatus.status === 'verified' && '✓ '}
                  {verificationStatus.status === 'error' && '⚠ '}
                  {verificationStatus.message}
                </p>
                {verificationStatus.data && (
                  <div className="mt-2 text-xs space-y-1">
                    <p><b>Proof Hash:</b> <span className="font-mono">{verificationStatus.data.proofHash}</span></p>
                    <p><b>Usuário:</b> <span className="font-mono">{verificationStatus.data.user}</span></p>
                    <p><b>Recompensa:</b> {ethers.formatEther(verificationStatus.data.reward)} RUA</p>
                    <p><b>Bloco:</b> {verificationStatus.data.blockNumber.toLocaleString()}</p>
                  </div>
                )}
              </div>
            )}

            {/* ✅ PRESERVADO: Resultado completo com design atualizado */}
            {result && (
              <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg text-sm space-y-3 text-black">
                {result.verified && (
                  <div className="bg-green-100 border border-green-500 rounded p-2 mb-3">
                    <p className="text-xs font-bold text-green-800">✓ VERIFICADO NA BLOCKCHAIN</p>
                  </div>
                )}

                <p><b>Mensagem:</b></p>
                <p className="p-3 bg-gray-100 rounded break-words font-mono text-sm">"{result.message}"</p>

                {result.videoUrl && (
                  <>
                    <p><b>Vídeo (Proof of Life):</b></p>
                    <video controls className="w-full rounded-lg mt-2" src={result.videoUrl} />
                  </>
                )}

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <p><b>Autor:</b> {result.author}</p>
                  <p><b>Timestamp:</b> {new Date(result.timestamp).toLocaleString()}</p>
                </div>

                {result.proofHash && (
                  <div className="bg-gray-100 rounded p-2 text-xs">
                    <p><b>Proof Hash (Blockchain):</b></p>
                    <p className="font-mono break-all text-violet-600">{result.proofHash}</p>
                  </div>
                )}

                {result.txHash && (
                  <p className="break-all text-xs">
                    <b>TX:</b>{' '}
                    <a 
                      href={`https://polygonscan.com/tx/${result.txHash}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-violet-600 underline hover:text-violet-700"
                    >
                      {result.txHash}
                    </a>
                  </p>
                )}

                {result.onChainData && (
                  <div className="bg-gray-100 rounded p-2 text-xs space-y-1">
                    <p><b>Dados On-Chain:</b></p>
                    <p><b>Contrato:</b> <span className="font-mono">{result.onChainData.contractAddress}</span></p>
                    <p><b>Recompensa:</b> {ethers.formatEther(result.onChainData.reward)} RUA</p>
                    <p><b>Bloco:</b> {result.onChainData.blockNumber.toLocaleString()}</p>
                  </div>
                )}

                <p className="text-xs opacity-70 italic text-center mt-4 text-gray-600">
                  with Grok services · Deus no centro
                </p>
              </div>
            )}
          </div>
        </main>

        {/* SIDEBAR DIREITA - ANÚNCIO (apenas desktop) */}
        <aside className="hidden lg:block flex-shrink-0">
          <AdPlaceholder position="sidebar" />
        </aside>
      </div>

      {/* RODAPÉ - BANNER DE ANÚNCIO */}
      <footer className="max-w-7xl mx-auto px-4 py-6 mt-8">
        <div className="flex justify-center">
          <AdPlaceholder position="footer" />
        </div>
      </footer>
    </div>
  );
};

export default ProofDecoder;
